import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR, { type KeyedMutator } from "swr";
import type {
  GetInheritanceReuseCandidatesAPIRequest,
  GetInheritanceReuseCandidatesAPIResponse,
} from "@/features/inheritanceReuse/types";
import { SendToTrackingService } from "@/api/handlers/errors";

const ENDPOINT = "/inheritance-reuse/candidates";

interface IResponse {
  data: GetInheritanceReuseCandidatesAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetInheritanceReuseCandidatesAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * 相続後リユース候補の一覧を取得する。
 * サーバサイドページング・ソート。都道府県未選択時（request undefined）は発火しない。
 * リクエストの camelCase キーは apiClient（axios-case-converter）が snake_case へ変換する。
 * @param {AxiosInstance} apiClient JWT 認証付き apiClient
 * @param {GetInheritanceReuseCandidatesAPIRequest | undefined} request リクエスト条件
 * @returns {IResponse}
 */
export const GetInheritanceReuseCandidatesAPI = (
  apiClient: AxiosInstance,
  request: GetInheritanceReuseCandidatesAPIRequest | undefined
): IResponse => {
  const fetcher = async (
    url: string,
    params: GetInheritanceReuseCandidatesAPIRequest
  ): Promise<GetInheritanceReuseCandidatesAPIResponse> => {
    const { data } =
      await apiClient.get<GetInheritanceReuseCandidatesAPIResponse>(url, {
        params,
      });
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetInheritanceReuseCandidatesAPIResponse,
    AxiosError
  >(
    request === undefined ? null : [ENDPOINT, request],
    async ([url, params]: [string, GetInheritanceReuseCandidatesAPIRequest]) =>
      await fetcher(url, params),
    {
      revalidateOnFocus: false,
    }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, mutate, error };
  }

  return { data, isLoading, mutate, error };
};
